import React, { useState, useEffect, useRef } from "react";

// ─── Color Data ───
const THEME_COLORS = [
  ["#FFFFFF", "#F2F2F2", "#D8D8D8", "#BFBFBF", "#A5A5A5", "#7F7F7F"],
  ["#000000", "#7F7F7F", "#595959", "#3F3F3F", "#262626", "#0C0C0C"],
  ["#E7E6E6", "#D0CECE", "#AEAAAA", "#757171", "#3B3838", "#161616"],
  ["#44546A", "#D6DCE4", "#ADB9CA", "#8497B0", "#333F4F", "#222B35"],
  ["#5B9BD5", "#DEEBF6", "#BDD7EE", "#9CC2E5", "#2E75B6", "#1E4E79"],
  ["#ED7D31", "#FBE5D5", "#F8CBAD", "#F4B083", "#C65911", "#833C0B"],
  ["#A5A5A5", "#EDEDED", "#DBDBDB", "#C9C9C9", "#7B7B7B", "#525252"],
  ["#FFC000", "#FFF2CC", "#FFE699", "#FFD966", "#BF9000", "#7F6000"],
  ["#4472C4", "#D9E2F3", "#B4C6E7", "#8EAADB", "#2F5496", "#1F3864"],
  ["#70AD47", "#E2EFDA", "#C5E0B3", "#A8D08D", "#538135", "#375623"],
];

const STANDARD_COLORS = [
  "#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050",
  "#00B050", "#00B0F0", "#0070C0", "#002060", "#7030A0", "#EEE7E7"
];

const _recentColorsStore = { list: [] };

const normHex = (c) => (c || '').toUpperCase();

const ColorPickerButton = ({ value, onChange, icon }) => {
  const [open, setOpen] = useState(false);
  const [recentColors, setRecentColors] = useState([..._recentColorsStore.list]);
  const [pendingColor, setPendingColor] = useState(null);
  const wrapperRef = useRef(null);
  const colorInputRef = useRef(null);
  const colorInputActive = useRef(false);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (colorInputActive.current) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setPendingColor(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const handleSelect = (color) => {
    const upper = color.toUpperCase();
    _recentColorsStore.list = [upper, ..._recentColorsStore.list.filter(c => c !== upper)].slice(0, 10);
    setRecentColors([..._recentColorsStore.list]);
    onChange(color);
    setOpen(false);
  };

  const openPicker = () => { if (colorInputRef.current) colorInputRef.current.click(); };

  return (
    <div ref={wrapperRef} className="position-relative d-inline-block">
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ev-btn-border-square position-relative"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {icon || <span>A</span>}

        {/* Color underline */}
        <div style={{
          width: "var(--px-15)", height: "var(--px-3)", backgroundColor: value || '#000',
          position: "absolute", bottom: "4px", left: "50%", transform: "translateX(-50%)"
        }}></div>
      </button>

      {open && (
        <div
          className="ev-message-card2 ev-card-3 position-absolute top-100 z-1000 min-w-300"
          style={{ right: "var(--px-0)" }}
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <p className="ev-fs-10 fw-bold mb-1">Theme Colors</p>

          {/* Theme Grid */}
          <div className="d-flex justify-content-between mb-2">
            {THEME_COLORS.map((column, colIdx) => (
              <div key={colIdx} className="d-flex flex-column gap-1">
                {column.map((color, rowIdx) => (
                  <button
                    key={`${colIdx}-${rowIdx}`}
                    type="button"
                    className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer ${normHex(value) === normHex(color) ? 'cpb-selected' : ''} ${rowIdx === 0 ? 'header-gap' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="border-top my-2"></div>

          <p className="ev-fs-10 fw-bold mb-1">Standard Colors</p>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {STANDARD_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer ${normHex(value) === normHex(color) ? 'cpb-selected' : ''}`}
                style={{ backgroundColor: color }}
                onClick={() => handleSelect(color)}
                title={color}
              />
            ))}
          </div>

          {recentColors.length > 0 && (
            <>
              <div className="border-top my-2"></div>
              <p className="ev-fs-10 fw-bold mb-1">Recently Used Colors</p>
              <div className="d-flex flex-wrap gap-1 mb-2">
                {recentColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer ${normHex(value) === normHex(color) ? 'cpb-selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            </>
          )}

          <div className="border-top my-2"></div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label
              className="d-flex align-items-center gap-2 cursor-pointer mb-0 position-relative"
            >
              <input
                ref={colorInputRef}
                type="color"
                className="rfm-color-input-hidden"
                defaultValue={value || '#000000'}
                onInput={(e) => setPendingColor(e.target.value)}
                onChange={(e) => setPendingColor(e.target.value)}
              />
              <i className="bi bi-palette-fill ev-fs-14" />
              <span className="ev-fs-11">More Colors...</span>
            </label>
            {pendingColor && (
              <div className="d-flex align-items-center gap-1 ms-auto">
                <div className="p-2 ev-radius-xsmall ev-card-4 cursor-pointer" style={{ backgroundColor: pendingColor }} onClick={openPicker} title={pendingColor} />
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-btn-xsmall"
                  onClick={(e) => { 
                      e.stopPropagation(); 
                      handleSelect(pendingColor); 
                      setPendingColor(null); 
                  }}
                >Save</button>
                <button
                  type="button"
                  className="ev-btn-red-fill-round ev-btn-xsmall"
                  onClick={(e) => { 
                      e.stopPropagation(); 
                      setPendingColor(null); 
                  }}
                >Cancel</button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default ColorPickerButton;